import Guid from "_shared/guids/guid";
import AHDate from "_shared/dates/date";
import MessageFileInfoSchema, { MessageFileInfoJSON, MessageFileInfoParams } from "messaging/attachments/_schemas/message-file-info-schema";
import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";

export default class MessageFileInfo {
  public static readonly EXAMPLE: MessageFileInfo = new MessageFileInfo({
    id: Guid.generate(),
    name: "example.txt",
    fileSize: 1024,
    mimeType: "text/plain",
    createdDate: new AHDate(),
    rawData: new Blob(["Example content"], { type: "text/plain" }),
    dataUri: "data:text/plain,Example%20content"
  });

  public static create(params: MessageFileInfoParams): MessageFileInfo {
    const parsed = MessageFileInfoSchema.validate(params);
    if (isNullOrUndefined(parsed)) {
      throw new Error("MessageFileInfo.Create(): Invalid parameters.");
    }
    return new MessageFileInfo(parsed);
  }

  public static fromJSON(json: MessageFileInfoJSON): MessageFileInfo {
    if (isNullOrUndefined(json)) return json;
    const params = MessageFileInfoSchema.deserialize(json);
    return new MessageFileInfo(params);
  }

  public static fromJSONArray(jsonArray: MessageFileInfoJSON[]): MessageFileInfo[] {
    return jsonArray.map(json => MessageFileInfo.fromJSON(json));
  }

  public static fromFile(file: File): MessageFileInfo {
    return new MessageFileInfo({
      id: Guid.generate(),
      name: file.name,
      fileSize: file.size,
      mimeType: file.type,
      createdDate: new AHDate(),
      rawData: file,
      dataUri: URL.createObjectURL(file)
    });
  }

  public static toFile(messageFileInfo: MessageFileInfo): File {
    return new File([messageFileInfo.dataUri!], messageFileInfo.name, {
      type: messageFileInfo.mimeType,
    });
  }

  public readonly id: Guid;
  public readonly name: string;
  public readonly fileSize: number;
  public readonly mimeType: string;
  public readonly createdDate: AHDate;
  public readonly rawData?: Blob;
  public readonly dataUri?: string;

  public get isImage(): boolean {
    return this.mimeType.startsWith("image/");
  }

  public constructor(params: MessageFileInfoParams) {
    this.id = params.id;
    this.name = params.name;
    this.fileSize = params.fileSize;
    this.mimeType = params.mimeType;
    this.createdDate = params.createdDate;
    this.rawData = params.rawData;
    this.dataUri = params.dataUri;
  }

  public toJSON(): MessageFileInfoJSON {
    return MessageFileInfoSchema.serialize(this);
  }

  public isEqualTo(other: MessageFileInfo | null | undefined): boolean {
    if (!other) return false;
    return this.id.isEqualTo(other.id);
  }

  public toString(): string {
    return `MessageFileInfo (
      id: ${this.id},
      name: ${this.name},
      fileSize: ${this.fileSize},
      mimeType: ${this.mimeType},
      createdDate: ${this.createdDate.toString()},
      rawData: ${this.rawData ? "[Blob Data]" : "undefined"},
      dataUri: ${this.dataUri ? "[Data URI]" : "undefined"}
    )`;
  }
}
